import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { 
  UserPlus, 
  Mail, 
  Loader2, 
  CheckCircle2, 
  AlertCircle,
  Link2,
  Send,
  User,
  Building2,
  Copy,
  Check
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function InviteExpertModal({ isOpen, onClose, authToken, onSuccess }) {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);

  const resetForm = () => {
    setEmail('');
    setMessage('');
    setResult(null);
    setLinkCopied(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email.trim()) {
      toast.error('Please enter a consultant email');
      return;
    }

    setIsLoading(true);
    setResult(null);

    try {
      const response = await fetch(`${API_URL}/api/consultant-invites/invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          email: email.trim(),
          message: message.trim() || null
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to invite consultant');
      }

      setResult(data);
      
      // Show appropriate toast
      if (data.consultant_status === 'EXISTING_LINKED') {
        toast.success(`${data.consultant_org_name || data.consultant_email} is now linked to your workspace!`);
      } else if (data.consultant_status === 'NEW_INVITED') {
        toast.success(`Invitation sent to ${data.consultant_email}`);
      } else {
        toast.info(data.message);
      }

      if (onSuccess) {
        onSuccess(data);
      }

    } catch (error) {
      console.error('Invite consultant error:', error);
      toast.error(error.message || 'Failed to invite consultant');
    } finally {
      setIsLoading(false);
    }
  };

  const renderResultContent = () => {
    if (!result) return null;

    const statusConfig = {
      'EXISTING_LINKED': {
        icon: CheckCircle2,
        iconClass: 'text-emerald-500',
        bgClass: 'bg-emerald-50',
        title: 'Consultant Linked!',
        description: 'They can now access your workspace and pursuits.'
      },
      'EXISTING_INVITED': {
        icon: AlertCircle,
        iconClass: 'text-amber-500',
        bgClass: 'bg-amber-50',
        title: 'Already Invited',
        description: 'An invitation is already pending for this consultant.'
      },
      'NEW_INVITED': {
        icon: Send,
        iconClass: 'text-blue-500',
        bgClass: 'bg-blue-50',
        title: 'Invitation Created!',
        description: 'Share the link below with your consultant to complete their registration.'
      }
    };

    const config = statusConfig[result.consultant_status] || statusConfig['NEW_INVITED'];
    const IconComponent = config.icon;
    
    // Generate invite link if token is available
    const inviteLink = result.invitation_token 
      ? `${window.location.origin}/invite/${result.invitation_token}`
      : null;
    
    const handleCopyLink = async () => {
      if (inviteLink) {
        await navigator.clipboard.writeText(inviteLink);
        setLinkCopied(true);
        toast.success('Invite link copied to clipboard!');
        setTimeout(() => setLinkCopied(false), 3000);
      }
    };

    return (
      <div className={`rounded-lg p-6 ${config.bgClass}`}>
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0">
            <IconComponent className={`w-8 h-8 ${config.iconClass}`} />
          </div>
          <div className="flex-1">
            <h4 className="font-semibold text-slate-900 mb-1">{config.title}</h4>
            <p className="text-sm text-slate-600 mb-3">{config.description}</p>
            
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-slate-400" />
                <span className="text-slate-700">{result.consultant_email}</span>
              </div>
              
              {result.consultant_name && (
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-slate-400" />
                  <span className="text-slate-700">{result.consultant_name}</span>
                </div>
              )}
              
              {result.consultant_org_name && (
                <div className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-slate-400" />
                  <span className="text-slate-700">{result.consultant_org_name}</span>
                </div>
              )}
            </div>
            
            {/* Copy Invite Link Section - Only for NEW_INVITED */}
            {inviteLink && result.consultant_status === 'NEW_INVITED' && (
              <div className="mt-4 pt-4 border-t border-blue-200">
                <Label className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2 block">
                  Shareable Invite Link
                </Label>
                <div className="flex items-center gap-2">
                  <Input
                    value={inviteLink}
                    readOnly
                    className="text-xs bg-white border-blue-200 font-mono"
                    data-testid="invite-link-input"
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className={`flex-shrink-0 ${linkCopied ? 'bg-emerald-50 border-emerald-300 text-emerald-700' : 'border-blue-300 text-blue-700 hover:bg-blue-50'}`}
                    onClick={handleCopyLink}
                    data-testid="copy-invite-link-btn"
                  >
                    {linkCopied ? (
                      <>
                        <Check className="w-4 h-4 mr-1" />
                        Copied
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4 mr-1" />
                        Copy
                      </>
                    )}
                  </Button>
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  Share this link privately. It expires in 14 days.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg" data-testid="invite-expert-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl font-bold text-slate-900">
            <UserPlus className="w-5 h-5 text-teal-600" />
            Invite Your Expert
          </DialogTitle>
          <DialogDescription>
            Invite an external consultant to collaborate on your pursuits. 
            If they're already on FedBD, they'll be linked immediately.
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <>
            {renderResultContent()}
            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={resetForm}>
                Invite Another
              </Button>
              <Button onClick={handleClose} className="bg-teal-600 hover:bg-teal-700">
                Done
              </Button>
            </DialogFooter>
          </>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="consultant-email">Consultant Email *</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="consultant-email"
                  type="email"
                  placeholder="consultant@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10"
                  data-testid="invite-expert-email"
                  required
                  autoFocus
                />
              </div>
              <p className="text-xs text-slate-500">
                Enter the email of the consultant you'd like to invite
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="invite-message">Personal Message (Optional)</Label>
              <Textarea
                id="invite-message"
                placeholder="Hi! I'd like to invite you to help with our federal contracting pursuits..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="resize-none min-h-[80px]"
                data-testid="invite-expert-message"
              />
            </div>

            <div className="bg-slate-50 rounded-lg p-4 border border-slate-100">
              <h4 className="text-sm font-medium text-slate-700 mb-2">What happens next?</h4>
              <ul className="text-xs text-slate-600 space-y-1.5">
                <li className="flex items-start gap-2">
                  <span className="text-teal-500 mt-0.5">•</span>
                  <span>If they're already a consultant on FedBD, they'll be linked to your workspace instantly</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-teal-500 mt-0.5">•</span>
                  <span>If they're new, they'll receive an invitation to register as a Consultant</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-teal-500 mt-0.5">•</span>
                  <span>Once linked, they can access your pursuits and provide expert guidance</span>
                </li>
              </ul>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-teal-600 hover:bg-teal-700"
                disabled={isLoading || !email.trim()}
                data-testid="invite-expert-submit"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Invitation
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
